import React from "react";
import { PodiumRewardsCard } from "../PodiumBadges";
import { Field, InfoCard, TextInput, Toggle } from "./WizardFields";
import {
  CashIcon,
  CheckIcon,
  LinkChainIcon,
  SlidersIcon,
  TrophyOutlineIcon,
} from "../icons";

// Step 3 of Create League — the league's rules — extracted so it can be shown on
// its own as "League settings" for a league that already exists.
//
// An organiser changing a rule should see the screen they set it on. Rebuilding
// these cards for the edit screen would mean two versions of the same decisions
// drifting apart the first time either changes, so both hosts render THIS.
//
// `chrome` is what differs between them:
//   showFairByDesign — the mobile-only reassurance card, a creation-flow thing.
//   showInviteHint   — "your invite code is coming up next", only true while
//                      creating; the code exists by the time you're editing.
const SettingsStep = ({
  variant,
  form,
  setForm,
  errors = {},
  cashEnabled = false,
  showFairByDesign = true,
  showInviteHint = true,
}) => (
  <div className="space-y-4">
    <InfoCard icon={<SlidersIcon />} title="League settings">
      <div className="space-y-4 mt-1">
        {/* The line under each label explains what the switch does. The
            league's own rules — the four fixed result strings — are what
            Review and every league screen show; see leagueFormat. */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h5 className="font-semibold text-sm text-[#0F172A]">Allow late joins</h5>
            <p className="mt-0.5 text-xs text-secondary">{variant.lateJoinsHint}</p>
          </div>
          <Toggle
            label="Allow late joins"
            checked={form.joinAfterStart}
            onChange={(v) => setForm((prev) => ({ ...prev, joinAfterStart: v }))}
          />
        </div>
        <div className="flex items-start justify-between gap-4 border-t border-divider pt-4">
          <div className="flex-1">
            <h5 className="font-semibold text-sm text-[#0F172A]">Hide portfolios</h5>
            <p className="mt-0.5 text-xs text-secondary">
              Members can't see each other's portfolios until the league ends.
            </p>
          </div>
          <Toggle
            label="Hide portfolios"
            checked={form.privatePortfolio}
            onChange={(v) => setForm((prev) => ({ ...prev, privatePortfolio: v }))}
          />
        </div>
      </div>
    </InfoCard>

    {/* Mobile only, by design: the phone screens carry this reassurance
        between the settings and the rewards, and the desktop ones don't. */}
    {showFairByDesign && (
      <div className="md:hidden rounded-2xl border border-gray-200 bg-white p-4 flex items-start gap-3">
        <span className="shrink-0 w-9 h-9 rounded-full bg-[#E9F7F0] flex items-center justify-center">
          <CheckIcon className="w-4 h-4" stroke="#22A36B" />
        </span>
        <div className="min-w-0">
          <h4 className="font-montserrat font-bold text-base text-[#1A1A1A]">
            Fair by design
          </h4>
          {/* Client Rev 1, Screen 3: "force the copy to two lines so
              'Tamper-proof.' begins on line 2. Do not split the word." The
              break is explicit rather than left to the container, and
              whitespace-nowrap stops the browser breaking the hyphenated word
              into "Tamper-" / "proof." at a narrower width. */}
          <p className="mt-0.5 text-sm text-secondary">
            Live data. Verified results.
            <br />
            <span className="whitespace-nowrap">Tamper-proof.</span>
          </p>
        </div>
      </div>
    )}

    {/* Podium rewards, full width — three badges don't stay legible in
        half a row. */}
    {variant.showPodiumRewards && <PodiumRewardsCard variantKey={variant.key} />}

    {/* Whatever the organiser puts up themselves, on top of the badges and
        coins every league already awards — hence "extra" on Review. */}
    {variant.showReward && (
      <InfoCard
        icon={<TrophyOutlineIcon className="w-7 h-7" stroke="#22A36B" />}
        title={variant.rewardTitle}
        description={variant.rewardDescription}
      >
        {/* Client Rev 2: "Add a prize needs a character limit - 60 characters
            maximum on Screen 3, with a visible counter such as 0/60." `max` is
            this flow's own convention for both: it caps the input and draws the
            live counter in the field's bottom-right corner, the same as League
            name and Tagline. */}
        <TextInput
          type="text"
          value={form.reward}
          max={60}
          placeholder={variant.rewardPlaceholder}
          invalid={!!errors.reward}
          onChange={(e) => setForm((prev) => ({ ...prev, reward: e.target.value }))}
        />
        {errors.reward && <p className="mt-1 text-xs text-red-500">{errors.reward}</p>}
      </InfoCard>
    )}

    {/* Cash prize. Not a league-type thing — the gate is the account: an
        admin turns on "Allow Posting Cash Prize League" for the user,
        checkCashUser reports it, and only then does this appear at all. */}
    {cashEnabled && (
      <InfoCard icon={<CashIcon className="w-7 h-7" />} title="Cash prize league">
        <div className="flex items-start justify-between gap-4">
          <p className="flex-1 text-sm text-secondary">
            Fund a cash prize for the winners of this league.
          </p>
          <Toggle
            label="Cash prize league"
            checked={form.cashLeague}
            onChange={(v) =>
              setForm((prev) => ({
                ...prev,
                cashLeague: v,
                cashPrizeAmount: v ? prev.cashPrizeAmount : "",
                // Turning the toggle off clears the podium split too, so a
                // league that stops being a cash game cannot keep payouts that
                // no longer have a pool to come out of.
                firstPlaceAmount: v ? prev.firstPlaceAmount : "",
                secondPlaceAmount: v ? prev.secondPlaceAmount : "",
                thirdPlaceAmount: v ? prev.thirdPlaceAmount : "",
              }))
            }
          />
        </div>
        {form.cashLeague && (
          <div className="mt-4 space-y-4">
            <Field label="Total prize pool" error={errors.cashPrizeAmount}>
              <TextInput
                type="number"
                min="1"
                value={form.cashPrizeAmount}
                placeholder="Enter cash prize amount"
                invalid={!!errors.cashPrizeAmount}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, cashPrizeAmount: e.target.value }))
                }
              />
            </Field>

            {/* The podium split, directly beneath the total — the client's own
                placement. 1st is required; 2nd and 3rd are optional and a blank
                one drops its row from the Cash Game landing page rather than
                printing a $0 nobody is playing for.

                The three must add up to the total prize pool. That is checked in
                validateStep (and again by the API, which will not publish a game
                whose podium disagrees with its pool), and the message lands under
                the 1st place field because that is the required one. */}
            <div className="rounded-xl border border-gray-200 bg-[#F8FAFA] p-4">
              <h5 className="font-semibold text-sm text-[#0F172A]">Prize breakdown</h5>
              <p className="mt-0.5 text-xs text-secondary">
                1st, 2nd and 3rd must add up to the total prize pool. 2nd and 3rd
                are optional.
              </p>

              <div className="mt-3 space-y-3">
                <Field label="1st place amount" error={errors.firstPlaceAmount}>
                  <TextInput
                    type="number"
                    min="1"
                    value={form.firstPlaceAmount}
                    placeholder="Top prize"
                    invalid={!!errors.firstPlaceAmount}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, firstPlaceAmount: e.target.value }))
                    }
                  />
                </Field>
                <Field label="2nd place amount (optional)" error={errors.secondPlaceAmount}>
                  <TextInput
                    type="number"
                    min="0"
                    value={form.secondPlaceAmount}
                    placeholder="Leave blank for no 2nd prize"
                    invalid={!!errors.secondPlaceAmount}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, secondPlaceAmount: e.target.value }))
                    }
                  />
                </Field>
                <Field label="3rd place amount (optional)" error={errors.thirdPlaceAmount}>
                  <TextInput
                    type="number"
                    min="0"
                    value={form.thirdPlaceAmount}
                    placeholder="Leave blank for no 3rd prize"
                    invalid={!!errors.thirdPlaceAmount}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, thirdPlaceAmount: e.target.value }))
                    }
                  />
                </Field>
              </div>
            </div>
          </div>
        )}
      </InfoCard>
    )}

    {variant.key === "private" && showInviteHint && (
      <InfoCard
        icon={<LinkChainIcon className="w-7 h-7" />}
        title="Invite settings"
        description="Your invite code is coming up next."
      />
    )}
  </div>
);

export default SettingsStep;
